import { useCallback, useEffect } from "react"

import { useSavePage } from "./use-save-page"
import { useSearchParamsPage } from "./use-search-params-page"

interface UsePageProps {
  /** A unique id to identify the year for purposes of saving */
  id: string
}

interface UsePageValue {
  /** The page */
  page: number

  /** Sets the page in both the url and its persistence layer */
  setPage: (value: number, options?: { replace?: boolean }) => void
}

const defaultPage = 1

const isPageValid = (page: number | null | undefined): boolean => {
  return (
    page !== undefined && page !== null && Number.isFinite(page) && page > 0
  )
}

/**
 * Manages the page value that powers a UI including search params,
 * saving the page, validity, and default value.
 *
 * @example
 * const {
 *   page,
 *   setPage
 * } = usePage({
 *   id: "sitesPerformancePage"
 * })
 */
export const usePage = ({ id }: UsePageProps): UsePageValue => {
  const { isPageInSearchParams, searchParamsPage, setSearchParamsPage } =
    useSearchParamsPage()
  const { savePage, savedPage } = useSavePage({ id })

  const page: number | null | undefined = isPageValid(searchParamsPage)
    ? searchParamsPage
    : savedPage

  const setPage = useCallback(
    (value: number, options?: { replace?: boolean }): void => {
      setSearchParamsPage(value, options)
      savePage(value)
    },
    [savePage, setSearchParamsPage]
  )

  // Handle missing search param and invalid page
  useEffect(() => {
    const newPage = isPageValid(page)
      ? (page as unknown as number)
      : defaultPage

    // Ensure correct page is in url
    if (!isPageInSearchParams) {
      setSearchParamsPage(newPage, { replace: true })
    }

    // Ensure page is saved if necessary
    if (newPage !== savedPage) {
      savePage(newPage)
    }
  }, [isPageInSearchParams, page, savePage, savedPage, setSearchParamsPage])

  return { page: page as unknown as number, setPage }
}
